import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import AdminLayout from './AdminLayout';
import { Plus, Edit2, Trash2, ToggleLeft, ToggleRight, X, ChevronDown, ChevronRight, Search, Filter } from 'lucide-react';
import '../styles/Admin.css';
import {
    getAllServices,
    createService,
    updateService,
    deleteService,
    toggleServiceStatus
} from '../ServiceAPI/AdminUser/serviceapi';
import {
    getFormFieldsByService,
    createFormField,
    updateFormField,
    deleteFormField,
    toggleFormFieldStatus
} from '../ServiceAPI/AdminUser/formfieldapi';

const ServiceManagement = () => {
    const navigate = useNavigate();
    const [services, setServices] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');
    const [showModal, setShowModal] = useState(false);
    const [editingService, setEditingService] = useState(null);
    const [editLevel, setEditLevel] = useState(''); // 'name', 'type', 'category'
    const [expandedNames, setExpandedNames] = useState(new Set());
    const [expandedTypes, setExpandedTypes] = useState(new Set());
    const [searchTerm, setSearchTerm] = useState("");
    const [statusFilter, setStatusFilter] = useState("all");
    const [showFormFieldsModal, setShowFormFieldsModal] = useState(false);
    const [selectedServiceForFields, setSelectedServiceForFields] = useState(null);
    const [formFields, setFormFields] = useState([]);
    const [loadingFormFields, setLoadingFormFields] = useState(false);

    const [formData, setFormData] = useState({
        Name: '',
        Type: '',
        Category: '',
        Status: 1
    });

    const [editingFormField, setEditingFormField] = useState(null);
    const [showFormFieldForm, setShowFormFieldForm] = useState(false);
    const [formFieldData, setFormFieldData] = useState({
        Field_label: '',
        Input_type: 'text',
        Is_required: 0,
        Options: '',
        Status: 1,
        Priority: 0
    });

    const inputTypes = [
        'text', 'email', 'password', 'number', 'tel', 'date', 'datetime-local',
        'time', 'url', 'search', 'color', 'range', 'textarea', 'select',
        'checkbox', 'radio', 'file', 'hidden'
    ];

    // Check authentication
    useEffect(() => {
        const isAdminAuthenticated = localStorage.getItem("adminAuth") === "true";
        if (!isAdminAuthenticated) {
            navigate("/admin/login");
            return;
        }
    }, [navigate]);

    // Fetch services
    useEffect(() => {
        const fetchData = async () => {
            try {
                setLoading(true);
                const servicesData = await getAllServices();

                setServices(Array.isArray(servicesData) ? servicesData : servicesData.data || []);
            } catch (err) {
                console.error('Error fetching data:', err);
                setError('Failed to load services');
            } finally {
                setLoading(false);
            }
        };

        fetchData();
    }, []);

    const handleInputChange = (e) => {
        const { name, value, type, checked } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: type === 'checkbox' ? (checked ? 1 : 0) : value
        }));
    };

    // Filter services based on search and status
    const filteredServices = services.filter(service => {
        // Search filter
        const searchLower = searchTerm.toLowerCase();
        const matchesSearch =
            !searchTerm ||
            (service.Name && service.Name.toLowerCase().includes(searchLower)) ||
            (service.Type && service.Type.toLowerCase().includes(searchLower)) ||
            (service.Category && service.Category.toLowerCase().includes(searchLower));

        // Status filter
        const matchesStatus =
            statusFilter === 'all' ||
            (statusFilter === 'active' && service.Status === 1) ||
            (statusFilter === 'inactive' && service.Status === 0);

        return matchesSearch && matchesStatus;
    });

    // Group filtered services
    const groupFilteredServices = () => {
        const grouped = {};

        filteredServices.forEach(service => {
            if (!grouped[service.Name]) {
                grouped[service.Name] = {};
            }
            if (!grouped[service.Name][service.Type]) {
                grouped[service.Name][service.Type] = [];
            }
            grouped[service.Name][service.Type].push(service);
        });

        return grouped;
    };

    const toggleName = (name) => {
        const newExpanded = new Set(expandedNames);
        if (newExpanded.has(name)) {
            newExpanded.delete(name);
        } else {
            newExpanded.add(name);
        }
        setExpandedNames(newExpanded);
    };

    const toggleType = (nameType) => {
        const newExpanded = new Set(expandedTypes);
        if (newExpanded.has(nameType)) {
            newExpanded.delete(nameType);
        } else {
            newExpanded.add(nameType);
        }
        setExpandedTypes(newExpanded);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError('');
        setSuccess('');

        try {
            const submitData = {
                ...formData,
                Status: parseInt(formData.Status)
            };

            let successMessage = '';

            if (editingService) {
                // If editing at name level, update all services with that name
                if (editLevel === 'name') {
                    const servicesToUpdate = services.filter(s => s.Name === editingService.oldName);
                    await Promise.all(servicesToUpdate.map(service =>
                        updateService(service.Id, {
                            ...service,
                            Name: submitData.Name,
                            Status: submitData.Status
                        })
                    ));
                    successMessage = `Successfully updated ${servicesToUpdate.length} service${servicesToUpdate.length > 1 ? 's' : ''} under "${editingService.oldName}"`;
                }
                // If editing at type level, update all services with that name and type
                else if (editLevel === 'type') {
                    const servicesToUpdate = services.filter(s =>
                        s.Name === editingService.Name && s.Type === editingService.oldType
                    );
                    await Promise.all(servicesToUpdate.map(service =>
                        updateService(service.Id, {
                            ...service,
                            Type: submitData.Type,
                            Status: submitData.Status
                        })
                    ));
                    successMessage = `Successfully updated ${servicesToUpdate.length} categor${servicesToUpdate.length > 1 ? 'ies' : 'y'} under "${editingService.Name} - ${editingService.oldType}"`;
                }
                // If editing at category level, update only that specific service
                else {
                    await updateService(editingService.Id, submitData);
                    successMessage = 'Service category updated successfully';
                }
            } else {
                await createService(submitData);
                successMessage = 'New service created successfully';
            }

            // Refresh data
            const servicesData = await getAllServices();
            setServices(Array.isArray(servicesData) ? servicesData : servicesData.data || []);

            // Show success message
            setSuccess(successMessage);

            // Reset form
            setFormData({
                Name: '',
                Type: '',
                Category: '',
                Status: 1
            });
            setEditingService(null);
            setEditLevel('');
            setShowModal(false);

            // Clear success message after 3 seconds
            setTimeout(() => setSuccess(''), 3000);
        } catch (err) {
            console.error('Error saving service:', err);
            setError(err.response?.data?.message || 'Failed to save service');
        }
    };

    const handleEditName = (name) => {
        const firstService = services.find(s => s.Name === name);
        setEditLevel('name');
        setEditingService({
            oldName: name,
            Name: name,
            Type: '',
            Category: '',
            Status: firstService?.Status || 1
        });
        setFormData({
            Name: name,
            Type: '',
            Category: '',
            Status: firstService?.Status || 1
        });
        setShowModal(true);
    };

    const handleEditType = (name, type) => {
        const firstService = services.find(s => s.Name === name && s.Type === type);
        setEditLevel('type');
        setEditingService({
            Name: name,
            oldType: type,
            Type: type,
            Category: '',
            Status: firstService?.Status || 1
        });
        setFormData({
            Name: name,
            Type: type,
            Category: '',
            Status: firstService?.Status || 1
        });
        setShowModal(true);
    };

    const handleEditCategory = (service) => {
        setEditLevel('category');
        setEditingService(service);
        setFormData({
            Name: service.Name,
            Type: service.Type,
            Category: service.Category,
            Status: service.Status
        });
        setShowModal(true);
    };

    const handleDelete = async (serviceId) => {
        if (window.confirm('Are you sure you want to delete this service? This action cannot be undone if the service has no associated data.')) {
            try {
                await deleteService(serviceId);
                const servicesData = await getAllServices();
                setServices(Array.isArray(servicesData) ? servicesData : servicesData.data || []);
                setError('');
            } catch (err) {
                console.error('Error deleting service:', err);
                setError(err.response?.data?.message || 'Failed to delete service');
            }
        }
    };

    const handleDeleteName = async (name) => {
        if (window.confirm(`Are you sure you want to delete all services under "${name}"? This will delete all types and categories.`)) {
            setError('');
            setSuccess('');
            try {
                const servicesToDelete = services.filter(s => s.Name === name);
                await Promise.all(servicesToDelete.map(service => deleteService(service.Id)));
                const servicesData = await getAllServices();
                setServices(Array.isArray(servicesData) ? servicesData : servicesData.data || []);
                setSuccess(`Successfully deleted ${servicesToDelete.length} service${servicesToDelete.length > 1 ? 's' : ''} under "${name}"`);
                setTimeout(() => setSuccess(''), 3000);
            } catch (err) {
                console.error('Error deleting services:', err);
                setError(err.response?.data?.message || 'Failed to delete services');
            }
        }
    };

    const handleDeleteType = async (name, type) => {
        if (window.confirm(`Are you sure you want to delete all categories under "${name} - ${type}"?`)) {
            setError('');
            setSuccess('');
            try {
                const servicesToDelete = services.filter(s => s.Name === name && s.Type === type);
                await Promise.all(servicesToDelete.map(service => deleteService(service.Id)));
                const servicesData = await getAllServices();
                setServices(Array.isArray(servicesData) ? servicesData : servicesData.data || []);
                setSuccess(`Successfully deleted ${servicesToDelete.length} categor${servicesToDelete.length > 1 ? 'ies' : 'y'} under "${name} - ${type}"`);
                setTimeout(() => setSuccess(''), 3000);
            } catch (err) {
                console.error('Error deleting services:', err);
                setError(err.response?.data?.message || 'Failed to delete services');
            }
        }
    };

    const handleToggleStatus = async (serviceId) => {
        try {
            await toggleServiceStatus(serviceId);
            const servicesData = await getAllServices();
            setServices(Array.isArray(servicesData) ? servicesData : servicesData.data || []);
            setError('');
        } catch (err) {
            console.error('Error toggling status:', err);
            setError('Failed to toggle status');
        }
    };

    const openCreateModal = async () => {
        setEditingService(null);
        setEditLevel('');
        setFormData({
            Name: '',
            Type: '',
            Category: '',
            Status: 1
        });

        setShowModal(true);
    };

    const handleViewFormFields = async (service) => {
        setSelectedServiceForFields(service);
        setShowFormFieldsModal(true);
        setLoadingFormFields(true);
        setShowFormFieldForm(false);
        setEditingFormField(null);

        try {
            const response = await getFormFieldsByService(service.Id);
            const fieldsData = response.data || [];
            setFormFields(Array.isArray(fieldsData) ? fieldsData : []);
        } catch (err) {
            console.error('Error fetching form fields:', err);
            setFormFields([]);
        } finally {
            setLoadingFormFields(false);
        }
    };

    const handleFormFieldInputChange = (e) => {
        const { name, value, type, checked } = e.target;
        setFormFieldData(prev => ({
            ...prev,
            [name]: type === 'checkbox' ? (checked ? 1 : 0) : value
        }));
    };

    const handleFormFieldSubmit = async (e) => {
        e.preventDefault();
        try {
            const submitData = {
                ...formFieldData,
                Service_id: selectedServiceForFields.Id,
                Is_required: parseInt(formFieldData.Is_required),
                Status: parseInt(formFieldData.Status),
                Priority: parseInt(formFieldData.Priority || 0, 10)
            };

            let savedField = null;
            if (editingFormField) {
                const response = await updateFormField(editingFormField.Id, submitData);
                savedField = response?.data || null;
            } else {
                const response = await createFormField(submitData);
                savedField = response?.data || null;
            }

            // Refresh fields list with a fresh server response
            const response = await getFormFieldsByService(selectedServiceForFields.Id);
            const nextFields = Array.isArray(response?.data) ? response.data : [];
            setFormFields(nextFields);

            if (savedField?.Id) {
                const fieldWithPriority = {
                    ...savedField,
                    Priority: Number(savedField.Priority ?? submitData.Priority ?? 0)
                };
                setFormFields(prevFields => {
                    const exists = prevFields.some(field => field.Id === fieldWithPriority.Id);
                    if (exists) {
                        return prevFields.map(field => field.Id === fieldWithPriority.Id ? fieldWithPriority : field);
                    }
                    return [fieldWithPriority, ...prevFields];
                });
            }

            setShowFormFieldForm(false);
            setEditingFormField(null);
            setFormFieldData({
                Field_label: '',
                Input_type: 'text',
                Is_required: 0,
                Options: '',
                Status: 1,
                Priority: 0
            });
        } catch (err) {
            console.error('Error saving form field:', err);
            alert('Failed to save form field');
        }
    };

    const handleEditFormField = (field) => {
        setEditingFormField(field);
        setFormFieldData({
            Field_label: field.Field_label,
            Input_type: field.Input_type,
            Is_required: field.Is_required,
            Options: field.Options || '',
            Status: field.Status,
            Priority: field.Priority || 0
        });
        setShowFormFieldForm(true);
    };

    const handleDeleteFormField = async (fieldId) => {
        if (window.confirm('Are you sure you want to delete this form field?')) {
            try {
                await deleteFormField(fieldId);
                const response = await getFormFieldsByService(selectedServiceForFields.Id);
                setFormFields(response.data || []);
            } catch (err) {
                console.error('Error deleting form field:', err);
                alert('Failed to delete form field');
            }
        }
    };

    const handleToggleFormFieldStatus = async (fieldId) => {
        try {
            await toggleFormFieldStatus(fieldId);
            const response = await getFormFieldsByService(selectedServiceForFields.Id);
            setFormFields(response.data || []);
        } catch (err) {
            console.error('Error toggling status:', err);
            alert('Failed to toggle status');
        }
    };

    if (loading) return (
        <AdminLayout>
            <div className="loading-container">
                <div className="spinner-large"></div>
                <p>Loading services...</p>
            </div>
        </AdminLayout>
    );

    return (
        <AdminLayout>
            <div className="admin-page-header">
                <div>
                    <h1 className="admin-page-title">Service Management</h1>
                    <p className="admin-page-subtitle">Create and manage RTO services</p>
                </div>
                <button
                    className="btn-primary-modern"
                    onClick={openCreateModal}
                >
                    <Plus size={20} />
                    Add New Service
                </button>
            </div>

            {error && (
                <div className="alert alert-error">
                    <span>{error}</span>
                    <button onClick={() => setError('')} className="alert-close">
                        <X size={16} />
                    </button>
                </div>
            )}

            {success && (
                <div className="alert alert-success">
                    <span>{success}</span>
                    <button onClick={() => setSuccess('')} className="alert-close">
                        <X size={16} />
                    </button>
                </div>
            )}

            <div className="admin-content-area">
                <div className="data-table-card">
                    <div className="data-table-header">
                        <h3>Services ({filteredServices.length} of {services.length})</h3>
                    </div>

                    {/* Search and Filter Section */}
                    <div className="search-filter-section">
                        <div className="search-box">
                            <Search size={20} />
                            <input
                                type="text"
                                placeholder="Search by Name, Type, Category..."
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                                className="search-input"
                            />
                        </div>
                        <div className="filter-group">
                            <Filter size={18} />
                            <select
                                value={statusFilter}
                                onChange={(e) => setStatusFilter(e.target.value)}
                                className="filter-select"
                            >
                                <option value="all">All Status</option>
                                <option value="active">Active</option>
                                <option value="inactive">Inactive</option>
                            </select>
                        </div>
                    </div>

                    <div className="hierarchical-services">
                        {filteredServices.length > 0 ? (
                            (() => {
                                const grouped = groupFilteredServices();
                                return Object.keys(grouped).map(serviceName => {
                                    const isNameExpanded = expandedNames.has(serviceName);
                                    const nameServices = filteredServices.filter(s => s.Name === serviceName);
                                    const nameStatus = nameServices.every(s => s.Status === 1);

                                    return (
                                        <div key={serviceName} className="service-level">
                                            {/* Service Name Level */}
                                            <div className="service-item service-name-item">
                                                <div className="service-item-left">
                                                    <button
                                                        className="expand-btn"
                                                        onClick={() => toggleName(serviceName)}
                                                    >
                                                        {isNameExpanded ? <ChevronDown size={20} /> : <ChevronRight size={20} />}
                                                    </button>
                                                    <div className="service-info">
                                                        <strong className="service-label">{serviceName}</strong>
                                                        <span className="service-count">{nameServices.length} items</span>
                                                    </div>
                                                </div>
                                                <div className="service-item-actions">
                                                    <span className={`status-badge-modern ${nameStatus ? 'active' : 'inactive'}`}>
                                                        {nameStatus ? 'Active' : 'Inactive'}
                                                    </span>
                                                    <button
                                                        className="action-btn primary"
                                                        onClick={() => handleEditName(serviceName)}
                                                        title="Edit Service Name"
                                                    >
                                                        <Edit2 size={16} />
                                                    </button>
                                                    <button
                                                        className="action-btn danger"
                                                        onClick={() => handleDeleteName(serviceName)}
                                                        title="Delete All"
                                                    >
                                                        <Trash2 size={16} />
                                                    </button>
                                                </div>
                                            </div>

                                            {/* Types under this Name */}
                                            {isNameExpanded && (
                                                <div className="service-children">
                                                    {Object.keys(grouped[serviceName]).map(serviceType => {
                                                        const typeKey = `${serviceName}-${serviceType}`;
                                                        const isTypeExpanded = expandedTypes.has(typeKey);
                                                        const typeServices = grouped[serviceName][serviceType];
                                                        const typeStatus = typeServices.every(s => s.Status === 1);

                                                        return (
                                                            <div key={typeKey} className="service-level">
                                                                {/* Service Type Level */}
                                                                <div className="service-item service-type-item">
                                                                    <div className="service-item-left">
                                                                        <button
                                                                            className="expand-btn"
                                                                            onClick={() => toggleType(typeKey)}
                                                                        >
                                                                            {isTypeExpanded ? <ChevronDown size={18} /> : <ChevronRight size={18} />}
                                                                        </button>
                                                                        <div className="service-info">
                                                                            <span className="service-label">{serviceType}</span>
                                                                            <span className="service-count">{typeServices.length} categories</span>
                                                                        </div>
                                                                    </div>
                                                                    <div className="service-item-actions">
                                                                        <span className={`status-badge-modern ${typeStatus ? 'active' : 'inactive'}`}>
                                                                            {typeStatus ? 'Active' : 'Inactive'}
                                                                        </span>
                                                                        <button
                                                                            className="action-btn primary"
                                                                            onClick={() => handleEditType(serviceName, serviceType)}
                                                                            title="Edit Type"
                                                                        >
                                                                            <Edit2 size={14} />
                                                                        </button>
                                                                        <button
                                                                            className="action-btn danger"
                                                                            onClick={() => handleDeleteType(serviceName, serviceType)}
                                                                            title="Delete All Categories"
                                                                        >
                                                                            <Trash2 size={14} />
                                                                        </button>
                                                                    </div>
                                                                </div>

                                                                {/* Categories under this Type */}
                                                                {isTypeExpanded && (
                                                                    <div className="service-children">
                                                                        {typeServices.map(service => (
                                                                            <div key={service.Id} className="service-item service-category-item">
                                                                                <div
                                                                                    className="service-item-left clickable-category"
                                                                                    onClick={() => handleViewFormFields(service)}
                                                                                    title="Click to view form fields"
                                                                                >
                                                                                    <div className="category-dot"></div>
                                                                                    <div className="service-info">
                                                                                        <span className="service-label">{service.Category}</span>
                                                                                    </div>
                                                                                </div>
                                                                                <div className="service-item-actions">
                                                                                    <span className={`status-badge-modern ${service.Status ? 'active' : 'inactive'}`}>
                                                                                        {service.Status ? 'Active' : 'Inactive'}
                                                                                    </span>
                                                                                    <button
                                                                                        className="action-btn primary"
                                                                                        onClick={() => handleEditCategory(service)}
                                                                                        title="Edit"
                                                                                    >
                                                                                        <Edit2 size={14} />
                                                                                    </button>
                                                                                    <button
                                                                                        className={`action-btn ${service.Status ? 'warning' : 'success'}`}
                                                                                        onClick={() => handleToggleStatus(service.Id)}
                                                                                        title={service.Status ? 'Deactivate' : 'Activate'}
                                                                                    >
                                                                                        {service.Status ? <ToggleRight size={14} /> : <ToggleLeft size={14} />}
                                                                                    </button>
                                                                                    <button
                                                                                        className="action-btn danger"
                                                                                        onClick={() => handleDelete(service.Id)}
                                                                                        title="Delete"
                                                                                    >
                                                                                        <Trash2 size={14} />
                                                                                    </button>
                                                                                </div>
                                                                            </div>
                                                                        ))}
                                                                    </div>
                                                                )}
                                                            </div>
                                                        );
                                                    })}
                                                </div>
                                            )}
                                        </div>
                                    );
                                });
                            })()
                        ) : (
                            <div className="empty-state">
                                <Plus size={48} />
                                <p>No services found</p>
                                <button className="btn-secondary-modern" onClick={openCreateModal}>
                                    Create your first service
                                </button>
                            </div>
                        )}
                    </div>
                </div>
            </div>

            {/* Form Fields Modal */}
            {showFormFieldsModal && (
                <div className="modal-overlay-modern">
                    <div className="modal-container-modern form-fields-modal">
                        <div className="modal-header-modern">
                            <div style={{ flex: 1 }}>
                                <h3>Form Fields</h3>
                                {selectedServiceForFields && (
                                    <p className="modal-subtitle">
                                        {selectedServiceForFields.Name} → {selectedServiceForFields.Type} → {selectedServiceForFields.Category}
                                    </p>
                                )}
                            </div>
                            <div className="modal-header-actions">
                                {!showFormFieldForm && (
                                    <button
                                        className="btn-primary-mini"
                                        onClick={() => {
                                            setEditingFormField(null);
                                            setFormFieldData({
                                                Field_label: '',
                                                Input_type: 'text',
                                                Is_required: 0,
                                                Options: '',
                                                Status: 1
                                            });
                                            setShowFormFieldForm(true);
                                        }}
                                    >
                                        <Plus size={16} />
                                        Add Field
                                    </button>
                                )}
                                <button
                                    className="modal-close-btn"
                                    onClick={() => setShowFormFieldsModal(false)}
                                >
                                    <X size={24} />
                                </button>
                            </div>
                        </div>

                        <div className="modal-body-modern">
                            {loadingFormFields ? (
                                <div className="loading-container">
                                    <div className="spinner-large"></div>
                                    <p>Loading form fields...</p>
                                </div>
                            ) : showFormFieldForm ? (
                                <div className="form-field-edit-view">
                                    <h4 className="section-title-mini">{editingFormField ? 'Edit Field' : 'Add New Field'}</h4>
                                    <form onSubmit={handleFormFieldSubmit} className="modal-form-modern compact">
                                        <div className="form-group-modern">
                                            <label>Field Label *</label>
                                            <input
                                                type="text"
                                                name="Field_label"
                                                value={formFieldData.Field_label}
                                                onChange={handleFormFieldInputChange}
                                                placeholder="e.g., Applicant Name"
                                                required
                                            />
                                        </div>
                                        <div className="form-row">
                                            <div className="form-group-modern">
                                                <label>Input Type *</label>
                                                <select
                                                    name="Input_type"
                                                    value={formFieldData.Input_type}
                                                    onChange={handleFormFieldInputChange}
                                                    required
                                                >
                                                    {inputTypes.map(type => (
                                                        <option key={type} value={type}>{type}</option>
                                                    ))}
                                                </select>
                                            </div>
                                            <div className="form-group-modern checkbox-group" style={{ marginTop: '2rem' }}>
                                                <label className="checkbox-label">
                                                    <input
                                                        type="checkbox"
                                                        name="Is_required"
                                                        checked={formFieldData.Is_required === 1}
                                                        onChange={handleFormFieldInputChange}
                                                    />
                                                    <span>Required</span>
                                                </label>
                                            </div>
                                        </div>
                                        <div className="form-group-modern">
                                            <label>Display Priority</label>
                                            <input
                                                type="number"
                                                name="Priority"
                                                value={formFieldData.Priority}
                                                onChange={handleFormFieldInputChange}
                                                placeholder="0"
                                                min="0"
                                            />
                                            <small className="form-hint">Lower numbers appear first.</small>
                                        </div>
                                        <div className="form-group-modern">
                                            <label>Options (JSON format)</label>
                                            <textarea
                                                name="Options"
                                                value={formFieldData.Options}
                                                onChange={handleFormFieldInputChange}
                                                placeholder='{"opt1": "Value 1"}'
                                                rows="2"
                                            />
                                        </div>
                                        <div className="modal-footer-modern" style={{ padding: '1rem 0 0 0' }}>
                                            <button type="button" className="btn-secondary-modern" onClick={() => setShowFormFieldForm(false)}>
                                                Back to List
                                            </button>
                                            <button type="submit" className="btn-primary-modern">
                                                {editingFormField ? 'Update Field' : 'Create Field'}
                                            </button>
                                        </div>
                                    </form>
                                </div>
                            ) : formFields.length > 0 ? (
                                <div className="form-fields-list">
                                    {formFields.map((field, index) => (
                                        <div key={field.Id || index} className="form-field-item">
                                            <div className="form-field-header">
                                                <div className="header-main">
                                                    <span className="field-number">{field.Priority ?? index + 1}</span>
                                                    <h4 className="field-label">{field.Field_label}</h4>
                                                </div>
                                                <div className="header-actions">
                                                    <span className={`status-badge-modern ${field.Status ? 'active' : 'inactive'}`}>
                                                        {field.Status ? 'Active' : 'Inactive'}
                                                    </span>
                                                    <div className="field-action-buttons">
                                                        <button
                                                            className="action-btn-mini primary"
                                                            onClick={() => handleEditFormField(field)}
                                                        >
                                                            <Edit2 size={14} />
                                                        </button>
                                                        <button
                                                            className={`action-btn-mini ${field.Status ? 'warning' : 'success'}`}
                                                            onClick={() => handleToggleFormFieldStatus(field.Id)}
                                                        >
                                                            {field.Status ? <ToggleRight size={14} /> : <ToggleLeft size={14} />}
                                                        </button>
                                                        <button
                                                            className="action-btn-mini danger"
                                                            onClick={() => handleDeleteFormField(field.Id)}
                                                        >
                                                            <Trash2 size={14} />
                                                        </button>
                                                    </div>
                                                </div>
                                            </div>
                                            <div className="form-field-details">
                                                <div className="field-detail-row">
                                                    <span className="detail-label">Input Type:</span>
                                                    <span className="detail-value field-type-badge">{field.Input_type}</span>
                                                </div>
                                                <div className="field-detail-row">
                                                    <span className="detail-label">Priority:</span>
                                                    <span className="detail-value">{field.Priority ?? 0}</span>
                                                </div>
                                                <div className="field-detail-row">
                                                    <span className="detail-label">Required:</span>
                                                    <span className={`detail-value ${field.Is_required ? 'text-required' : 'text-optional'}`}>
                                                        {field.Is_required ? 'Yes' : 'No'}
                                                    </span>
                                                </div>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            ) : (
                                <div className="empty-state">
                                    <p>No form fields configured for this service</p>
                                    <button
                                        className="btn-primary-modern"
                                        style={{ marginTop: '1rem' }}
                                        onClick={() => setShowFormFieldForm(true)}
                                    >
                                        Add Your First Field
                                    </button>
                                </div>
                            )}
                        </div>

                        <div className="modal-footer-modern">
                            <button
                                type="button"
                                className="btn-secondary-modern"
                                onClick={() => setShowFormFieldsModal(false)}
                            >
                                Close
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Modal */}
            {showModal && (
                <div className="modal-overlay-modern">
                    <div className="modal-container-modern">
                        <div className="modal-header-modern">
                            <h3>
                                {editingService
                                    ? editLevel === 'name'
                                        ? 'Edit Service Name'
                                        : editLevel === 'type'
                                            ? 'Edit Service Type'
                                            : 'Edit Service Category'
                                    : 'Add New Service'}
                            </h3>
                            <button
                                className="modal-close-btn"
                                onClick={() => setShowModal(false)}
                            >
                                <X size={24} />
                            </button>
                        </div>

                        <form onSubmit={handleSubmit} className="modal-form-modern">
                            {/* Show appropriate fields based on edit level */}
                            {(!editLevel || editLevel === 'name' || !editingService) && (
                                <div className="form-group-modern">
                                    <label>Service Name {editLevel === 'name' && <span className="field-note">(Updates all types & categories)</span>}</label>
                                    <input
                                        type="text"
                                        name="Name"
                                        value={formData.Name}
                                        onChange={handleInputChange}
                                        placeholder="e.g., Driving License, Vehicle Registration"
                                        required
                                        disabled={editLevel === 'type' || editLevel === 'category'}
                                    />
                                </div>
                            )}

                            {(!editLevel || editLevel === 'type' || editLevel === 'category' || !editingService) && editLevel !== 'name' && (
                                <div className="form-group-modern">
                                    <label>Type {editLevel === 'type' && <span className="field-note">(Updates all categories under this type)</span>}</label>
                                    <input
                                        type="text"
                                        name="Type"
                                        value={formData.Type}
                                        onChange={handleInputChange}
                                        placeholder="e.g., New, Renewal"
                                        required={!editLevel || editLevel !== 'name'}
                                        disabled={editLevel === 'category'}
                                    />
                                </div>
                            )}

                            {(!editLevel || editLevel === 'category' || !editingService) && editLevel !== 'name' && editLevel !== 'type' && (
                                <div className="form-group-modern">
                                    <label>Category</label>
                                    <input
                                        type="text"
                                        name="Category"
                                        value={formData.Category}
                                        onChange={handleInputChange}
                                        placeholder="e.g., 4-Wheeler, 2-Wheeler"
                                        required={!editLevel}
                                    />
                                </div>
                            )}

                            <div className="form-group-modern checkbox-group">
                                <label className="checkbox-label">
                                    <input
                                        type="checkbox"
                                        name="Status"
                                        checked={formData.Status === 1}
                                        onChange={handleInputChange}
                                    />
                                    <span>Active Status {editLevel && editingService && <span className="field-note">(Applies to all affected items)</span>}</span>
                                </label>
                            </div>

                            {editLevel && editingService && (
                                <div className="alert alert-info">
                                    {editLevel === 'name' && `This will update the service name for all ${services.filter(s => s.Name === editingService.oldName).length} items under "${editingService.oldName}"`}
                                    {editLevel === 'type' && `This will update the type for all ${services.filter(s => s.Name === editingService.Name && s.Type === editingService.oldType).length} categories under "${editingService.Name} - ${editingService.oldType}"`}
                                    {editLevel === 'category' && `This will update only this specific category`}
                                </div>
                            )}

                            <div className="modal-footer-modern">
                                <button type="button" className="btn-secondary-modern" onClick={() => setShowModal(false)}>
                                    Cancel
                                </button>
                                <button type="submit" className="btn-primary-modern">
                                    {editingService ? 'Update Service' : 'Create Service'}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}
        </AdminLayout>
    );
};

export default ServiceManagement;
